import { ENTER } from '@angular/cdk/keycodes';
import { Component, inject, input, OnInit, output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

import { translate } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { AppSharedModule } from 'app/app-shared.module';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';

// eslint-disable-next-line
export interface ChooseTagsOptions {
  content: string;
  existingTags?: string[];
  placeholder: string;
  buttonText?: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-remove-tags-dialog',
  templateUrl: './remove-tags-dialog.component.html',
  styleUrls: ['./remove-tags-dialog.component.scss'],
  imports: [AppSharedModule],
})
export class RemoveTagsDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly options = input<ChooseTagsOptions>();
  readonly modalClosed = output<string[] | void>();
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  buttonText: string;
  tags: string[] = [];

  selectable = false;
  removable = true;
  separatorKeysCodes = [ENTER];

  chipInput = new UntypedFormControl('');
  filteredChipOptions?: Observable<string[]>;

  private alertService = inject(AlertService);

  constructor() {}

  ngOnInit() {
    if (this.options().existingTags) {
      this.filteredChipOptions = this.chipInput.valueChanges.pipe(
        startWith(''),
        map((value) => {
          const filterValue = value.toLowerCase();
          return this.options().existingTags.filter((option) =>
            option.toLowerCase().includes(filterValue)
          );
        }),
        map((values) => values.filter((value) => !this.tags.includes(value)))
      );
    }
  }

  public get hasError(): boolean {
    return this.tags.length === 0;
  }

  public cancel(): void {
    this.modalClosed.emit();
  }

  public async confirm(): Promise<void> {
    if (this.hasError) {
      return;
    }
    this.modalClosed.emit(this.tags);
  }

  onTextPasted($event: ClipboardEvent) {
    $event.preventDefault();
    $event.clipboardData
      .getData('Text')
      .split('\n')
      .forEach((value) => {
        this.addTag(value, <HTMLInputElement>$event.target);
      });
  }

  addTag(tagName: string, inputEl: HTMLInputElement): void {
    const value = Utils.replaceNonPrintable(tagName.trim());

    if (!value) {
      return;
    }

    if (this.options().existingTags && !this.options().existingTags.includes(value)) {
      this.alertService.showToast(translate('remove-tags-dialog.tags-not-exist-in-datasource'));
    }

    this.tags.push(value);
    this.autocomplete.closePanel();
    this.chipInput.setValue('');
    inputEl.value = '';
  }

  removeTag(tag: string): void {
    const index = this.tags.indexOf(tag);

    if (index >= 0) {
      this.tags.splice(index, 1);
    }

    if (this.options().existingTags) {
      this.chipInput.updateValueAndValidity();
    }
  }
}
